/**
 * VAT Calculation Utility
 * Single source of truth for VAT calculations in the POS system
 * 
 * All prices in the database are stored as VAT-inclusive (gross)
 * This utility provides functions to split gross amounts into net + VAT
 * for display purposes throughout the application.
 */

/**
 * Default VAT rate for KSA (15%)
 * Can be overridden per tenant in the future
 */
export const DEFAULT_VAT_RATE = 0.15

/**
 * Round to 2 decimal places (for currency)
 */
function roundCurrency(amount: number): number {
  return Math.round(amount * 100) / 100
}

/**
 * Split a VAT-inclusive amount into net and VAT components
 * 
 * @param grossAmount - The VAT-inclusive (gross) amount
 * @param vatRate - VAT rate (default 0.15 for 15%)
 * @returns Object with net, vat, and gross amounts
 * 
 * @example
 * splitVatInclusive(115.00) // { net: 100.00, vat: 15.00, gross: 115.00 }
 */
export function splitVatInclusive(
  grossAmount: number,
  vatRate: number = DEFAULT_VAT_RATE
): { net: number; vat: number; gross: number } {
  if (grossAmount < 0) {
    throw new Error('Gross amount cannot be negative')
  }
  if (vatRate < 0 || vatRate >= 1) {
    throw new Error('VAT rate must be between 0 and 1')
  }

  // Calculate net: net = gross / (1 + vatRate)
  const net = roundCurrency(grossAmount / (1 + vatRate))
  
  // Calculate VAT: vat = gross - net
  const vat = roundCurrency(grossAmount - net)
  
  // Ensure net + vat == gross (handle rounding differences)
  const calculatedGross = roundCurrency(net + vat)
  const roundingDiff = roundCurrency(grossAmount - calculatedGross)
  
  // Adjust net to account for rounding difference
  const adjustedNet = roundCurrency(net + roundingDiff)
  
  return {
    net: adjustedNet,
    vat: vat,
    gross: grossAmount
  }
}

/**
 * Calculate line item amounts from unit gross price and quantity
 * 
 * @param unitGross - Unit price (VAT-inclusive)
 * @param quantity - Quantity
 * @param vatRate - VAT rate (default 0.15)
 * @returns Object with unitNet, lineNet, lineVat, and lineGross
 * 
 * @example
 * lineFromGross(115.00, 2) 
 * // { unitNet: 100.00, lineNet: 200.00, lineVat: 30.00, lineGross: 230.00 }
 */
export function lineFromGross(
  unitGross: number,
  quantity: number,
  vatRate: number = DEFAULT_VAT_RATE
): { unitNet: number; lineNet: number; lineVat: number; lineGross: number } {
  if (quantity <= 0) {
    throw new Error('Quantity must be greater than 0')
  }

  const { net: unitNet, vat: unitVat } = splitVatInclusive(unitGross, vatRate)
  
  // Calculate line totals
  const lineGross = roundCurrency(unitGross * quantity)
  const lineNet = roundCurrency(unitNet * quantity)
  const lineVat = roundCurrency(unitVat * quantity)
  
  // Ensure line totals reconcile
  const calculatedLineGross = roundCurrency(lineNet + lineVat)
  const roundingDiff = roundCurrency(lineGross - calculatedLineGross)
  const adjustedLineNet = roundCurrency(lineNet + roundingDiff)
  
  return {
    unitNet,
    lineNet: adjustedLineNet,
    lineVat,
    lineGross
  }
}

/**
 * Calculate order totals from line items
 * 
 * @param lines - Array of line items with gross amounts
 * @param vatRate - VAT rate (default 0.15)
 * @returns Object with netTotal, vatTotal, and grossTotal
 */
export function totalsFromLines(
  lines: Array<{ gross: number; quantity?: number }>,
  vatRate: number = DEFAULT_VAT_RATE
): { netTotal: number; vatTotal: number; grossTotal: number } {
  let netTotal = 0
  let vatTotal = 0
  let grossTotal = 0

  for (const line of lines) {
    const gross = line.gross || 0
    const quantity = line.quantity || 1
    
    const { net, vat } = splitVatInclusive(gross, vatRate)
    
    netTotal = roundCurrency(netTotal + (net * quantity))
    vatTotal = roundCurrency(vatTotal + (vat * quantity))
    grossTotal = roundCurrency(grossTotal + (gross * quantity))
  }

  // Ensure totals reconcile
  const calculatedGross = roundCurrency(netTotal + vatTotal)
  const roundingDiff = roundCurrency(grossTotal - calculatedGross)
  netTotal = roundCurrency(netTotal + roundingDiff)

  return {
    netTotal,
    vatTotal,
    grossTotal
  }
}

/**
 * Apply discount to gross amount and recalculate net/VAT proportionally
 * 
 * @param grossAmount - Original gross amount
 * @param discountGross - Discount amount (applied to gross)
 * @param vatRate - VAT rate (default 0.15)
 * @returns Object with netDiscount, vatDiscount, discountGross, and resulting amounts
 */
export function applyDiscountToGross(
  grossAmount: number,
  discountGross: number,
  vatRate: number = DEFAULT_VAT_RATE
): {
  netDiscount: number
  vatDiscount: number
  discountGross: number
  netAfterDiscount: number
  vatAfterDiscount: number
  grossAfterDiscount: number
} {
  if (discountGross < 0) {
    throw new Error('Discount cannot be negative')
  }
  if (discountGross > grossAmount) {
    throw new Error('Discount cannot exceed gross amount')
  }

  // Split original gross into net and VAT
  const { net: originalNet, vat: originalVat } = splitVatInclusive(grossAmount, vatRate)
  
  // Calculate discount proportions
  const netDiscount = roundCurrency(discountGross / (1 + vatRate))
  const vatDiscount = roundCurrency(discountGross - netDiscount)
  
  // Calculate amounts after discount
  const grossAfterDiscount = roundCurrency(grossAmount - discountGross)
  const netAfterDiscount = roundCurrency(originalNet - netDiscount)
  const vatAfterDiscount = roundCurrency(originalVat - vatDiscount)
  
  // Ensure reconciliation
  const calculatedGross = roundCurrency(netAfterDiscount + vatAfterDiscount)
  const roundingDiff = roundCurrency(grossAfterDiscount - calculatedGross)
  const adjustedNet = roundCurrency(netAfterDiscount + roundingDiff)

  return {
    netDiscount,
    vatDiscount,
    discountGross,
    netAfterDiscount: adjustedNet,
    vatAfterDiscount,
    grossAfterDiscount
  }
}

/**
 * Calculate order totals with discounts
 * 
 * @param lines - Array of line items
 * @param orderDiscountGross - Order-level discount (gross)
 * @param vatRate - VAT rate (default 0.15)
 * @returns Object with all totals including discount breakdown
 */
export function calculateOrderTotals(
  lines: Array<{ gross: number; quantity?: number; discountGross?: number }>,
  orderDiscountGross: number = 0,
  vatRate: number = DEFAULT_VAT_RATE
): {
  subtotalNet: number
  subtotalVat: number
  subtotalGross: number
  lineDiscountsNet: number
  lineDiscountsVat: number
  lineDiscountsGross: number
  orderDiscountNet: number
  orderDiscountVat: number
  orderDiscountGross: number
  netTotal: number
  vatTotal: number
  grossTotal: number
} {
  // Calculate subtotals from lines (before discounts)
  let subtotalNet = 0
  let subtotalVat = 0
  let subtotalGross = 0
  let lineDiscountsNet = 0
  let lineDiscountsVat = 0
  let lineDiscountsGross = 0

  for (const line of lines) {
    const lineGross = (line.gross || 0) * (line.quantity || 1)
    const lineDiscount = (line.discountGross || 0) * (line.quantity || 1)
    
    const { net, vat } = splitVatInclusive(lineGross, vatRate)
    subtotalNet = roundCurrency(subtotalNet + net)
    subtotalVat = roundCurrency(subtotalVat + vat)
    subtotalGross = roundCurrency(subtotalGross + lineGross)
    
    if (lineDiscount > 0) {
      const { netDiscount, vatDiscount } = applyDiscountToGross(lineGross, lineDiscount, vatRate)
      lineDiscountsNet = roundCurrency(lineDiscountsNet + netDiscount)
      lineDiscountsVat = roundCurrency(lineDiscountsVat + vatDiscount)
      lineDiscountsGross = roundCurrency(lineDiscountsGross + lineDiscount)
    }
  }

  // Apply order-level discount
  const orderDiscount = applyDiscountToGross(subtotalGross - lineDiscountsGross, orderDiscountGross, vatRate)
  
  // Calculate final totals
  const netTotal = roundCurrency(subtotalNet - lineDiscountsNet - orderDiscount.netDiscount)
  const vatTotal = roundCurrency(subtotalVat - lineDiscountsVat - orderDiscount.vatDiscount)
  const grossTotal = roundCurrency(subtotalGross - lineDiscountsGross - orderDiscountGross)

  // Ensure reconciliation
  const calculatedGross = roundCurrency(netTotal + vatTotal)
  const roundingDiff = roundCurrency(grossTotal - calculatedGross)
  const adjustedNet = roundCurrency(netTotal + roundingDiff)

  return {
    subtotalNet,
    subtotalVat,
    subtotalGross,
    lineDiscountsNet,
    lineDiscountsVat,
    lineDiscountsGross,
    orderDiscountNet: orderDiscount.netDiscount,
    orderDiscountVat: orderDiscount.vatDiscount,
    orderDiscountGross,
    netTotal: adjustedNet,
    vatTotal,
    grossTotal
  }
}

/**
 * Get VAT rate from restaurant settings or use default
 * This should be called from components that need the current VAT rate
 */
export async function getVatRate(): Promise<number> {
  // This will be implemented to fetch from restaurant_settings
  // For now, return default
  return DEFAULT_VAT_RATE
}








